import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { addEdge, applyEdgeChanges, applyNodeChanges, Background, Connection, Controls, Edge, EdgeChange, MarkerType, MiniMap, Node, NodeChange, Panel, ReactFlow, ReactFlowProvider } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import './style.css';

import { Button, Col, Divider, Modal, notification, Row, Typography } from 'antd';
import LayoutDashboard from './../../layout/LayoutAdmin';

import { Loading } from '../../components/Loading';
import useConfiguracaoSimulador from './useConfiguracaoSimulador';
import { RobotOutlined } from '@ant-design/icons';
import MessageNode from './components/nodes/MessageNode';
import CollectNode from './components/nodes/CollectNode';
import OptionsNode from './components/nodes/OptionsNode';
import FieldNode from './components/nodes/FieldNode';
import ComparisonNode from './components/nodes/ComparisonNode';
import NodeFormDrawer from './components/NodeFormDrawer';
import ResultNode from './components/nodes/ResultNode';
import CustomEdge from './components/CustomEdge';
import { EItemFormatType, EItemType, IItem, IItemComparison, IItemField, IItemOption } from '../SimuladorInteligente/interfaces';
import FluxoService from '../../services/http/simulador/FluxoService';
import CadastroModal from './components/CadastroModal';
import { CustomNode, IDefaultInputInstruction, IDefaultMessage } from './interfaces';
import { EmitirErro } from '../../services/EmitirErro';
import { SairDoSistema } from '../../services/LStorage/SairSistema';
import { useNavigate } from 'react-router-dom';
import { AxiosError } from 'axios';
import DefaultMessageService from '../../services/http/simulador/DefaultMessageService';
import DefaultInputInstructionService from '../../services/http/simulador/DefaultInputInstructionService';

export type NodeToLinkData = {
    source: string;
    sourceHandle: string;
    parentPosition?: {
        x: number | undefined;
        y: number | undefined;
    },
    parentWidth?: number;
    offsetTop?: number;
};

const { Title } = Typography;

const edgeTypes = {
    deletable: CustomEdge,
}

const ConfiguracaoSimulador: React.FC = () => {
    const [isCadastrosOptionsModalVisible, setIsCadastrosOptionsModalVisible] = useState<boolean>(false);
    const [sourceNodeToLink, setSourceNodeToLink] = useState<NodeToLinkData | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [nodes, setNodes] = useState<Node[]>([]);
    const [edges, setEdges] = useState<Edge[]>([]);
    const [defaultMessages, setDefaultMessages] = useState<IDefaultMessage[]>([]);
    const [defaultInputInstructions, setDefaultInputInstructions] = useState<IDefaultInputInstruction[]>([]);

    const navigate = useNavigate();

    const {
        setIsFormDrawerVisible,
        isFormDrawerVisible,
    } = useConfiguracaoSimulador();

    const handlePressAddLinkedNode = useCallback((data: NodeToLinkData) => {
        setSourceNodeToLink(data);
        handleOpenFormDrawer();
    }, []);

    const handleDeselectAllNodes = useCallback(() => {
        setNodes((prevState) => prevState.map((node) => ({
            ...node,
            selected: false,
        })))
    }, []);

    const handleOpenFormDrawer = useCallback(() => {
        setIsFormDrawerVisible(true);
        handleDeselectAllNodes();
    }, []);

    const handleCloseFormDrawer = useCallback(() => {
        setIsFormDrawerVisible(false);
        setSourceNodeToLink(null);
    }, []);

    const nodeTypes = useMemo(() => {
        return {
            message: (props: any) => (
                <MessageNode
                    onPressAddLinkedNode={handlePressAddLinkedNode}
                    defaultMessages={defaultMessages}
                    defaultInputInstructions={defaultInputInstructions}
                    {...props}
                />
            ),
            collect: (props: any) => (
                <CollectNode
                    onPressAddLinkedNode={handlePressAddLinkedNode}
                    defaultMessages={defaultMessages}
                    defaultInputInstructions={defaultInputInstructions}
                    {...props}
                />
            ),
            options: (props: any) => (
                <OptionsNode
                    onPressAddLinkedNode={handlePressAddLinkedNode}
                    defaultMessages={defaultMessages}
                    defaultInputInstructions={defaultInputInstructions}
                    {...props}
                />
            ),
            field: (props: any) => (
                <FieldNode
                    onPressAddLinkedNode={handlePressAddLinkedNode}
                    defaultMessages={defaultMessages}
                    defaultInputInstructions={defaultInputInstructions}
                    {...props}
                />
            ),
            comparison: (props: any) => (
                <ComparisonNode
                    onPressAddLinkedNode={handlePressAddLinkedNode}
                    defaultMessages={defaultMessages}
                    defaultInputInstructions={defaultInputInstructions}
                    {...props}
                />
            ),
            result: (props: any) => (
                <ResultNode
                    onPressAddLinkedNode={handlePressAddLinkedNode}
                    defaultMessages={defaultMessages}
                    defaultInputInstructions={defaultInputInstructions}
                    {...props}
                />
            ),
        }
    }, [defaultMessages, defaultInputInstructions]);

    const handleAddNode = useCallback((node: CustomNode) => {
        setNodes((prevState) => {
            const newNode = node;

            if (
                sourceNodeToLink?.parentPosition &&
                sourceNodeToLink.parentPosition.x !== undefined &&
                sourceNodeToLink.parentWidth !== undefined &&
                sourceNodeToLink.parentPosition.y !== undefined
            ) {
                newNode.position.x = sourceNodeToLink.parentWidth + sourceNodeToLink.parentPosition.x + 50;
                newNode.position.y = sourceNodeToLink.parentPosition.y + (sourceNodeToLink.offsetTop || 0);
            }

            return prevState.concat(newNode)
        });

        if (node.type === EItemType.COMPARISON && node.data.comparisons?.some((e) => !!e.line_of_credit_id)) {
            node.data.comparisons.forEach((e) => {
                if (!e.line_of_credit_id) {
                    return;
                }

                setEdges((prevState) => addEdge({
                    source: node.id,
                    sourceHandle: e.id,
                    target: e.line_of_credit_id as string,
                    targetHandle: null,
                    animated: true,
                    markerEnd: {
                        type: MarkerType.ArrowClosed,
                    },
                    type: "deletable"
                }, prevState))
            });
        }

        if (sourceNodeToLink) {
            setEdges((prevState) => addEdge({
                source: sourceNodeToLink.source,
                sourceHandle: sourceNodeToLink.sourceHandle,
                target: node.id,
                targetHandle: null,
                animated: true,
                markerEnd: {
                    type: MarkerType.ArrowClosed,
                },
                type: "deletable"
            }, prevState))
        }
    }, [sourceNodeToLink]);

    const handleNodesChange = useCallback(
        (changes: NodeChange<Node>[]) => setNodes((nds) => applyNodeChanges(changes, nds)),
        [],
    );

    const handleEdgesChange = useCallback(
        (changes: EdgeChange<Edge>[]) => setEdges((eds) => applyEdgeChanges(changes, eds)),
        [],
    );

    const handleConnect = useCallback(
        (params: Edge | Connection) => setEdges((eds) => addEdge({
            ...params, animated: true,
            markerEnd: {
                type: MarkerType.ArrowClosed,
            },
            type: "deletable"
        }, eds)),
        [],
    );

    const handleSave = useCallback(async () => {
        const hasInitialNode = !!nodes.find((node) => node.data.is_initial_item);

        if (!hasInitialNode) {
            Modal.warning({
                title: 'Não é possível salvar o fluxo atual.',
                content: 'É necessário definir o item inicial do fluxo.',
            });

            return;
        }


        const finalItems: IItem[] = nodes.map((node) => {
            const findEdge = (handle: string) =>
                edges.find((e) => e.source === node.id && e.sourceHandle === handle)?.target;

            const messageText: string =
                node.data.default_message
                    ? defaultMessages.find((m) => m._id === node.data.default_message)?.message!
                    : node.data.message as string;

            const inputInstructions =
                node.data.default_input_instructions
                    ? defaultInputInstructions.find((m) => m._id === node.data.default_input_instructions)?.message
                    : node.data.input_instructions;

            const defaultComparisonValue = node.type === EItemType.COMPARISON
                ? findEdge(`comparison${node.data.label}default`)
                : undefined;

            return {
                id: node.id,
                is_initial_item: node.data.is_initial_item as boolean,
                allow_multiple_options: node.data.allow_multiple_options as boolean | undefined,
                message_text: messageText,
                type: node.type as EItemType,
                next: findEdge("a") || (node.type === EItemType.MESSAGE ? null : undefined),
                default: defaultComparisonValue,
                comparisons: (node.data?.comparisons as IItemComparison[] | undefined)?.map((c) => ({
                    title: c.title,
                    conditions: c.conditions,
                    next: findEdge(c.id),
                    line_of_credit_id: c.line_of_credit_id,
                    order: c.order,
                })) as IItemComparison[] ?? [],
                options: (node.data?.options as IItemOption[] | undefined)?.map((o) => ({
                    text: o.text,
                    description: o?.description,
                    next: findEdge(o.text),
                })) as IItemOption[] ?? [],
                fields: node.data.fields as IItemField[],
                format: node.data.format as EItemFormatType,
                line_of_credit: node.data.line_of_credit as string | undefined,
                placeholder: node.data.placeholder as string | undefined,
                values_from: node.data.values_from as string[] | undefined,
                input_instructions: inputInstructions as string | undefined,
                table_display_name: node.data.table_display_name as string | undefined,
                link_text: node.data.link_text as string | undefined,
                link: node.data.link as string | undefined,
                order: node.data.order as number | undefined,
                currency_min_value: node.data.currency_min_value as number | undefined,
                currency_max_value: node.data.currency_max_value as number | undefined,
            };
        });

        const childSourcesMap = new Map<string, Set<string>>();

        for (const edge of edges) {
            const target = edge.target;
            const source = edge.source;

            if (!childSourcesMap.has(target)) {
                childSourcesMap.set(target, new Set([source]));
            } else {
                childSourcesMap.get(target)!.add(source);
            }
        }

        const childToParentMap = new Map<string, string | null>();

        childSourcesMap.forEach((sources, target) => {
            if (sources.size === 1) {
                childToParentMap.set(target, Array.from(sources)[0]);
            } else {
                childToParentMap.set(target, null);
            }
        });

        const itemMap = new Map<string, IItem & { itens: IItem[] }>();

        finalItems.forEach((item) => itemMap.set(item.id, { ...item, itens: [] }));

        childToParentMap.forEach((parentId, childId) => {
            if (parentId && itemMap.has(childId) && itemMap.has(parentId)) {
                const parent = itemMap.get(parentId)!;
                const child = itemMap.get(childId)!;
                parent.itens.push(child);
            }
        });

        const rootNodes = finalItems
            .filter((item) => {
                const parent = childToParentMap.get(item.id);
                return parent == null;
            })
            .map((item) => itemMap.get(item.id)!);

        const hasNeverReachedNodes = rootNodes.some((node) => !node.is_initial_item && !edges.find((e) => e.target === node.id));

        if (hasNeverReachedNodes) {
            Modal.warning({
                title: 'Existem um ou mais itens que nunca serão alcançados pois não possuem uma ligação de entrada.',
                content: 'Deseja salvar o fluxo mesmo assim?',
                cancelText: "Cancelar",
                okText: "Salvar",
                okCancel: true,
                onCancel: () => { },
                onOk: () => saveConfig({
                    nodes,
                    edges,
                    itens: rootNodes,
                }),
            });

            return;
        }

        saveConfig({
            nodes,
            edges,
            itens: rootNodes,
        });
    }, [nodes, edges, defaultMessages, defaultInputInstructions]);

    const saveConfig = useCallback(async (data: {
        nodes: CustomNode[],
        edges: Edge[],
        itens: IItem[],
    }) => {
        try {
            await FluxoService.create(data);

            notification.success({
                message: 'Fluxo atualizado com sucesso!',
                description:
                    'A nova versão já está disponível para os clientes.',
            });
        } catch (error) {
            EmitirErro(error, true, 'configSimulador_dkwdfaiweyi')
        }
    }, []);

    const handleSubmitMessage = useCallback(async (values: Omit<IDefaultMessage, "_id">) => {
        try {
            const createdMessage = await DefaultMessageService.create(values);

            setDefaultMessages((prevState) => {
                return prevState.concat(createdMessage);
            });

            return true;

        } catch (error) {
            let msgErro: any = (error as Error);

            if (msgErro?.message === 'USUáRIO NãO AUTORIZADO.') {
                SairDoSistema();
                notification.error({
                    message: 'Sessão expirada',
                    description:
                        'Sua sessão expirou, realize o login novamente!',
                });
                navigate('/');
            } else {
                EmitirErro(error, true, 'configuracaoSimulador_dkwdfaiweyi')
            }

            return false;
        }

    }, []);

    const handleSubmitInputInstruction = useCallback(async (values: Omit<IDefaultInputInstruction, "_id">) => {
        try {
            const createdInstruction = await DefaultInputInstructionService.create(values);

            setDefaultInputInstructions((prevState) => {
                return prevState.concat(createdInstruction);
            });

            return true;

        } catch (error) {
            let msgErro: any = (error as Error);

            if (msgErro?.message === 'USUáRIO NãO AUTORIZADO.') {
                SairDoSistema();
                notification.error({
                    message: 'Sessão expirada',
                    description:
                        'Sua sessão expirou, realize o login novamente!',
                });
                navigate('/');
            } else {
                EmitirErro(error, true, 'configuracaoSimulador_dkwdfaiweyi')
            }

            return false;
        }
    }, []);

    useEffect(() => {
        const controller = new AbortController();

        async function loadEdgesAndNodes() {
            try {
                const {
                    edges,
                    nodes,
                } = await FluxoService.getEdgesAndNodes(controller.signal);

                if (nodes && edges) {
                    setNodes(nodes);
                    setEdges(edges);
                }

                setIsLoading(false);
            } catch (error) {
                let msgErro: any = (error as Error);

                if (error instanceof AxiosError && error.name === "CanceledError") {
                    setIsLoading(false);
                }

                if (msgErro?.message === 'USUáRIO NãO AUTORIZADO.') {
                    SairDoSistema();
                    notification.error({
                        message: 'Sessão expirada',
                        description:
                            'Sua sessão expirou, realize o login novamente!',
                    });
                    navigate('/');
                } else {
                    EmitirErro(error, true, 'configuracaoSimulador_dkwdfaiweyi')
                }
            }
        }

        loadEdgesAndNodes();

        return () => {
            controller.abort();
        }
    }, []);

    useEffect(() => {
        const controller = new AbortController();

        async function loadData() {
            try {
                const [
                    messages,
                    instructions
                ] = await Promise.all([
                    DefaultMessageService.get(controller.signal),
                    DefaultInputInstructionService.get(controller.signal),
                ]);

                setDefaultMessages(messages);
                setDefaultInputInstructions(instructions);
            } catch (error) {
                let msgErro: any = (error as Error);

                if (error instanceof AxiosError && error.name === "CanceledError") {
                    setIsLoading(false);
                }

                if (msgErro?.message === 'USUáRIO NãO AUTORIZADO.') {
                    SairDoSistema();
                    notification.error({
                        message: 'Sessão expirada',
                        description:
                            'Sua sessão expirou, realize o login novamente!',
                    });
                    navigate('/');
                } else {
                    EmitirErro(error, true, 'configuracaoSimulador_dkwdfaiweyi')
                }
            }
        }

        loadData();

        return () => {
            controller.abort();
        }
    }, []);

    return (
        <LayoutDashboard>
            <Loading
                loading={isLoading}
            >
                <Row>
                    <Col span={24}>
                        <Title level={4}>
                            <RobotOutlined style={{ fontSize: 22, marginRight: 10 }} />
                            {"Configuração Simulador Inteligente"}
                        </Title>
                        <Divider />
                    </Col>
                </Row>
            </Loading>

            <ReactFlowProvider>
                <Row
                    style={{
                        width: "100%",
                        height: "85%",
                        borderRadius: 8,
                        overflow: 'hidden',
                        marginBottom: 16,
                    }}
                >
                    <ReactFlow
                        nodes={nodes}
                        edges={edges}
                        nodeTypes={nodeTypes}
                        edgeTypes={edgeTypes}
                        onNodesChange={handleNodesChange}
                        onEdgesChange={handleEdgesChange}
                        onConnect={handleConnect}
                        minZoom={0.3}
                        style={{ backgroundColor: "#F7F9FB" }}
                    >
                        <Background />
                        <Controls position='top-left' />
                        <MiniMap
                            nodeColor={"#ffffff"}
                            bgColor='#bcd0e4'
                            pannable
                            inversePan
                        />
                        <Panel position='bottom-left' >
                            <Button
                                onClick={() => setIsCadastrosOptionsModalVisible(true)}
                                style={{
                                    marginLeft: 16,
                                }}
                            >
                                <span>Cadastro de itens padrão</span>
                            </Button>

                            <Button
                                type='primary'
                                onClick={handleOpenFormDrawer}
                                style={{
                                    marginLeft: 16,
                                }}
                            >
                                <span>Adicionar item</span>
                            </Button>

                            <Button
                                type='primary'
                                className='botaoVerdeBaixarTodas'
                                onClick={handleSave}
                                style={{
                                    marginLeft: 16,
                                }}
                            >
                                <span>Salvar</span>
                            </Button>
                        </Panel>
                    </ReactFlow>
                </Row>

                <NodeFormDrawer
                    visible={isFormDrawerVisible}
                    onClose={handleCloseFormDrawer}
                    onSubmit={handleAddNode}
                    defaultInputInstructions={defaultInputInstructions}
                    defaultMessages={defaultMessages}
                />

                <CadastroModal
                    visible={isCadastrosOptionsModalVisible}
                    onClose={() => setIsCadastrosOptionsModalVisible(false)}
                    inputInstructions={defaultInputInstructions}
                    messages={defaultMessages}
                    onSubmitInputInstruction={handleSubmitInputInstruction}
                    onSubmitMessage={handleSubmitMessage}
                />
            </ReactFlowProvider>
        </LayoutDashboard >
    );
}

export default ConfiguracaoSimulador;
